import { type FC, useState, useEffect } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { PLATFORM_COLORS, PLATFORM_ICONS, SAMPLE_SIGNALS } from '@/services/social-signals';
import type { SocialSignal } from '@/types';

// ---------------------------------------------------------------------------
// Social Signal Intelligence Panel
// Live stream = real Telegram OSINT signals only.
// Static samples render under an explicit SAMPLE divider (no timestamps).
// ---------------------------------------------------------------------------

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

function confidenceColor(score: number): string {
  if (score >= 85) return 'text-emerald-400';
  if (score >= 70) return 'text-yellow-400';
  if (score >= 50) return 'text-orange-400';
  return 'text-red-400';
}

const SignalCard: FC<{ signal: SocialSignal; sample?: boolean }> = ({ signal, sample }) => {
  const platformColor = PLATFORM_COLORS[signal.platform] ?? '#94a3b8';
  const platformIcon = PLATFORM_ICONS[signal.platform] ?? '?';

  return (
    <div className={clsx(
      'rounded p-2 border transition-colors',
      sample
        ? 'border-slate-800/60 bg-surface-panel/20 opacity-70'
        : signal.isFirstReport
        ? 'border-amber-800/50 bg-amber-950/20'
        : 'border-slate-800/60 bg-surface-panel/30',
    )}>
      {/* First Report badge — live only (no pulse / no timestamp on samples) */}
      {signal.isFirstReport && !sample && (
        <div className="flex items-center gap-1 mb-1">
          <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-amber-900/60 text-amber-300 border border-amber-800/50 uppercase tracking-wide animate-pulse">
            ⚡ First Report
          </span>
          <span className="text-[8px] text-slate-500">{timeAgo(signal.timestamp)}</span>
        </div>
      )}

      {/* Platform + author + time */}
      <div className="flex items-center gap-1.5 mb-1">
        <span
          className="text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded"
          style={{ backgroundColor: platformColor + '30', color: platformColor }}
        >
          {platformIcon}
        </span>
        <span className="text-[9px] text-slate-400 truncate flex-1">{signal.author}</span>
        {sample ? (
          <span className="text-[8px] px-1 rounded bg-slate-800 text-slate-500 uppercase">Sample</span>
        ) : !signal.isFirstReport && (
          <span className="text-[8px] text-slate-600">{timeAgo(signal.timestamp)}</span>
        )}
        <span className={clsx('text-[9px] font-bold tabular-nums', confidenceColor(signal.confidence))}>
          {signal.confidence}%
        </span>
      </div>

      {/* Signal text */}
      <p className="text-[10px] text-slate-200 leading-relaxed">
        {signal.text.length > 140 ? signal.text.slice(0, 140) + '…' : signal.text}
      </p>

      {/* Metadata */}
      <div className="flex items-center gap-2 mt-1">
        {signal.country && (
          <span className="text-[8px] text-slate-500">{signal.country}</span>
        )}
        <span className={clsx(
          'text-[8px] px-1 rounded uppercase',
          signal.severity === 'critical' ? 'bg-red-950/50 text-red-400' :
          signal.severity === 'high'     ? 'bg-orange-950/50 text-orange-400' :
          'bg-slate-800 text-slate-500',
        )}>{signal.severity}</span>
        <span className="text-[8px] text-slate-600 ml-auto">
          {(signal.engagementScore / 1000).toFixed(0)}K eng.
        </span>
      </div>
    </div>
  );
};

export const SocialSignalPanel: FC = () => {
  const socialSignals = useAppStore((s) => s.socialSignals);
  const [filter, setFilter] = useState<'all' | 'first' | 'critical'>('all');
  // Force re-render every 30s so timeAgo() stays current
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const filtered = socialSignals
    .filter((s) => {
      if (filter === 'first') return s.isFirstReport;
      if (filter === 'critical') return s.severity === 'critical';
      return true;
    })
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const firstReportCount = socialSignals.filter((s) => s.isFirstReport).length;
  const criticalCount = socialSignals.filter((s) => s.severity === 'critical').length;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-amber-400">📡 Signal Feed</span>
          {firstReportCount > 0 && (
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-amber-950/60 text-amber-300 border border-amber-800/40">
              {firstReportCount} FIRST
            </span>
          )}
        </div>
        <span className="text-[9px] text-slate-600">Telegram OSINT · Live</span>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 px-3 shrink-0">
        {(['all', 'first', 'critical'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={clsx(
              'px-2 py-0.5 rounded text-[9px] font-medium transition-colors border',
              filter === f
                ? 'bg-amber-900/50 text-amber-300 border-amber-800'
                : 'text-slate-500 border-slate-800 hover:text-slate-300',
            )}
          >
            {f === 'all'      ? `All (${socialSignals.length})` :
             f === 'first'    ? `⚡ First (${firstReportCount})` :
             `🔴 Critical (${criticalCount})`}
          </button>
        ))}
      </div>

      {/* Signal stream */}
      <div className="overflow-y-auto flex-1 px-3 pb-2 space-y-1.5 mt-1">
        {filtered.length === 0 && (
          <div className="text-[9px] text-slate-600 py-2 text-center">
            No live signals in this view — Telegram OSINT feed idle
          </div>
        )}
        {filtered.map((signal) => (
          <SignalCard key={signal.id} signal={signal} />
        ))}

        {/* SAMPLE section — illustrative only, never enters the live/alert path */}
        {filter === 'all' && SAMPLE_SIGNALS.length > 0 && (
          <>
            <div className="flex items-center gap-2 pt-2">
              <div className="flex-1 h-px bg-slate-800" />
              <span className="text-[9px] uppercase tracking-[0.2em] text-slate-600">
                Sample · Illustrative — not real posts
              </span>
              <div className="flex-1 h-px bg-slate-800" />
            </div>
            {SAMPLE_SIGNALS.map((signal) => (
              <SignalCard key={signal.id} signal={signal} sample />
            ))}
          </>
        )}
      </div>
    </div>
  );
};
